import {
  SystemCode,
  COLLEGE_PLAN_TYPES,
  FundNoteExchange,
  FundNoteExchangeCollege,
} from '../../../settings/settings';

import {
  checkFoundFeaturesByCode,
  DISABLE_ON_MATCH,
  hasRequiredServiceFeatures,
  updateFundNote,
} from '../shared/shared-fund-notes-utility';

/**
 * Creates function for looking up exchange specific server codes.
 * @param {UserFund} fund
 * @param {Number} serviceCode
 * @returns {boolean}
 */
function checkFoundFeaturesByCodeForExchange(fund, serviceCode) {
  return checkFoundFeaturesByCode(fund, serviceCode, true);
}

export function updateCollegeExchangeFromFundNotesForFund(fund) {
  // Address Undeliverable
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_MAIL_ID) &&
      !fund.addressChangedSameDay,
    FundNoteExchangeCollege.ADDRESS_UNDELIVERABLE,
    DISABLE_ON_MATCH
  );

  // Zero balance
  updateFundNote(
    fund,
    fund.balanceAsOf === 0,
    FundNoteExchangeCollege.FROM_ZERO,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.tooMany529Exchanges === true,
    FundNoteExchangeCollege.EXCHANGE_LIMIT_REACHED,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop All
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_ALL
    ),
    FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_ALL,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Exchange
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE
    ),
    FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_EXCHANGE,
    DISABLE_ON_MATCH
  );

  // Disable ACT Portfolio for Alaska 529 plan
  updateFundNote(
    fund,
    fund.productType === COLLEGE_PLAN_TYPES.ALASKA_529 &&
      fund.trpCode === 'CSA',
    FundNoteExchangeCollege.ALASKA_ACT_RESTRICTION,
    DISABLE_ON_MATCH
  );

  // Two Exchanges per year limit
  updateFundNote(
    fund,
    fund.can529ExchangeBePerformedWithinTimeframe,
    FundNoteExchangeCollege.TWO_EXCHANGES_PER_YEAR,
    DISABLE_ON_MATCH
  );

  // Pending Transaction
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars > 0,
    FundNoteExchangeCollege.PENDING
  );

  // Transaction pending and no redeemable funds available
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars === 0,
    FundNoteExchangeCollege.PENDING_TRANSACTION,
    DISABLE_ON_MATCH
  );

  // Return mail verify address
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.RETURN_MAIL_VERIFY_ADDRESS
    ),
    FundNoteExchangeCollege.RETURN_MAIL_VERIFY_ADDRESS,
    DISABLE_ON_MATCH
  );

  // Stop transfer
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_TRANSFER_ID) ||
      (fund['529FundMerged'] && fund.equityAsOf > 0),
    FundNoteExchangeCollege.STOP_TRANSFER,
    DISABLE_ON_MATCH
  );

  // Dividend Check (srv_id = 18 or srv_id = 17)
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.DIVIDEND_MOVE) ||
      checkFoundFeaturesByCode(
        fund,
        SystemCode.DIVIDEND_CHECK_TO_ALTERNATIVE_ADDRESS
      ),
    FundNoteExchange.WITHIN_CURRENT_INVESTMENTS
  );

  // ACH Dividend Service Feature (srv_id = 47)
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.ACH_DIVIDEND),
    FundNoteExchange.ACH_DIVIDEND
  );

  // Missing required service features
  updateFundNote(
    fund,
    !hasRequiredServiceFeatures(fund),
    FundNoteExchangeCollege.UNAVAILABLE,
    DISABLE_ON_MATCH
  );

  // Unavailable for redemption online
  updateFundNote(
    fund,
    false, // Disable this fund note to be consistent with MFEX behavior
    FundNoteExchangeCollege.UNAVAILABLE_FOR_REDEMPTION_ONLINE,
    DISABLE_ON_MATCH
  );
}

export function updateCollegeExchangeToFundNotesForFund(fund) {
  // Automated Systems Stop All
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_ALL
    ),
    FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_ALL,
    DISABLE_ON_MATCH
  );

  // Autmoated Systems Stop Check
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_CHECK
    ),
    FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_CHECK,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_PURCHASE
    ),
    FundNoteExchangeCollege.AUTOMATED_SYSTEMS_STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Exchange
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE
    ),
    FundNoteExchangeCollege.TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE,
    DISABLE_ON_MATCH
  );

  // Return Mail - Verify Address
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.RETURN_MAIL_VERIFY_ADDRESS
    ),
    FundNoteExchangeCollege.RETURN_MAIL_VERIFY_ADDRESS,
    DISABLE_ON_MATCH
  );

  // Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(fund, SystemCode.STOP_PURCHASE) ||
      fund['529FundMerged'],
    FundNoteExchangeCollege.STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Transaction Pending
  updateFundNote(
    fund,
    fund.hasPendingTransaction,
    FundNoteExchangeCollege.PENDING_TRANSACTION,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    !fund.newFund && !fund.canExchange,
    FundNoteExchange.NOT_ELIGIBLE,
    DISABLE_ON_MATCH
  );
}
